import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';

import { CsvAsStringArrays, getReviewAnalysisCategories, ReviewAnalysisTag } from '@malou-io/package-utils';

import { CategoryPerformanceSummaryCsvData } from '#modules/statistics-e-reputation/components/aggregated-e-reputation/semantic-analysis-v2/models/category-performance-summary';
import { AggregatedSemanticAnalysisExportService } from '#modules/statistics-e-reputation/services/aggregated-csv/aggregated-semantic-analysis-export.service';
import { LocationCategoryService } from '#modules/statistics-e-reputation/services/location-category.service';
import { AggregatedStatisticsFiltersContext } from '#modules/statistics/components/aggregated-statistics-filters/aggregated-statistics-filters.context';
import { AbstractCsvService } from '#modules/statistics/components/download-insights-modal/models/csv-service.abstract';
import { AggregatedStatisticsStore } from '#modules/statistics/stores/aggregated-store/aggregated-statistics.store';

type Data = CategoryPerformanceSummaryCsvData[] | null;

const CSV_CATEGORY_LABELS: Partial<Record<ReviewAnalysisTag, string>> = {
    [ReviewAnalysisTag.FOOD]: 'Food',
    [ReviewAnalysisTag.SERVICE]: 'Service',
    [ReviewAnalysisTag.ATMOSPHERE]: 'Ambiance',
    [ReviewAnalysisTag.PRICE]: 'Price',
    [ReviewAnalysisTag.HYGIENE]: 'Hygiene',
    [ReviewAnalysisTag.LOCATION]: 'Location',
    [ReviewAnalysisTag.ROOM_COMFORT]: 'Comfort',
    [ReviewAnalysisTag.RESTAURATION]: 'Dining',
    [ReviewAnalysisTag.BAR]: 'Bar',
    [ReviewAnalysisTag.AMENITIES]: 'Amenities',
    [ReviewAnalysisTag.ARRIVAL_LOGISTICS]: 'Logistics',
};

@Injectable()
export class AggregatedSemanticAnalysisCategoryPerformanceInsightsService extends AbstractCsvService<Data> {
    private readonly _aggregatedStatisticsStore = inject(AggregatedStatisticsStore);
    private readonly _aggregatedStatisticsFiltersContext = inject(AggregatedStatisticsFiltersContext);
    private readonly _locationCategoryService = inject(LocationCategoryService);
    private readonly _aggregatedSemanticAnalysisExportService = inject(AggregatedSemanticAnalysisExportService);
    private readonly _categoryPerformanceSummaryData$ = toObservable(
        this._aggregatedStatisticsStore.semanticAnalysisV2CategoryPerformanceSummaryData
    );

    protected _getData$(): Observable<Data> {
        return this._aggregatedSemanticAnalysisExportService.waitForData$(this._categoryPerformanceSummaryData$);
    }

    protected override _isDataValid(data: Data): boolean {
        return !!data;
    }

    protected override _getCsvHeaderRow(): string[] {
        return [
            'Restaurant Name',
            '# Negative mentions',
            'Evolution negative mentions',
            '# Positive mentions',
            'Evolution positive mentions',
            '% Negative mentions - Overall',
            '% Positive mentions - Overall',
            ...this._getCsvCategories().flatMap((category) => {
                const label = CSV_CATEGORY_LABELS[category] ?? category;
                return [`% Negative mentions - ${label}`, `% Positive mentions - ${label}`];
            }),
        ];
    }

    protected override _getCsvDataRows(data: CategoryPerformanceSummaryCsvData[]): CsvAsStringArrays {
        const categories = this._getCsvCategories();
        return data.map((item) => [
            item.restaurantName,
            item.allCategories.negativeMentions.toString(),
            item.allCategories.negativeMentionsEvolution?.toString() ?? '-',
            item.allCategories.positiveMentions.toString(),
            item.allCategories.positiveMentionsEvolution?.toString() ?? '-',
            `${item.allCategories.negativeMentionsPercentage ?? '-'}%`,
            `${item.allCategories.positiveMentionsPercentage ?? '-'}%`,
            ...categories.flatMap((category) => [
                `${item.mentionsByCategory[category].negativeMentionsPercentage ?? '-'}%`,
                `${item.mentionsByCategory[category].positiveMentionsPercentage ?? '-'}%`,
            ]),
        ]);
    }

    // The restaurant file never had a delivery column, and its columns must not change.
    private _getCsvCategories(): ReviewAnalysisTag[] {
        const locationCategory = this._locationCategoryService.getLocationCategoryForSelection(
            this._aggregatedStatisticsFiltersContext.selectedRestaurants()
        );
        return getReviewAnalysisCategories(locationCategory, { isDeliveryReleased: false });
    }
}
